import classNames from "classnames";
import * as React from "react";

const HighlightedCodeBody = React.lazy(
  () => import("@/shared/components/HighlightedCode/HighlightedCodeBody"),
);

import {
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  Info,
  LoaderCircle,
  type LucideIcon,
} from "@/shared/foundation/icons";
import {
  StatusBadge,
  type StatusTone,
  Tooltip,
} from "@/shared/foundation/primitives";
import { formatByteSize } from "@/shared/text";

import { type ToolCallStatus } from "../liveToolCalls";

import { ArtifactStrip } from "./ArtifactStrip";
import { CodeBlock } from "./CodeBlock";
import { JsonSection } from "./JsonSection";
import { type ArtifactOutput, notReturnedReason } from "./outputsField";
import styles from "./RunPythonCard.module.css";
import { RUN_PYTHON_OUTCOME_LOOK } from "./runPythonLook";
import { RunPythonOutput } from "./RunPythonOutput";
import {
  describeRunPythonDuration,
  describeRunPythonExit,
  describeRunPythonLivePhase,
  extractRunPythonArguments,
  extractRunPythonCode,
  formatClock,
  formatLimit,
  formatRunPythonDuration,
  type RunPythonCallSummary,
  type RunPythonLivePhase,
  type RunPythonPayload,
  summarizeRunPythonCall,
} from "./runPythonPayload";

/** A call the model has made that has no result yet. */
export interface RunPythonLiveState {
  kind: "live";
  status: ToolCallStatus;
  /** The thread's activity, naming the running call's phase. */
  activity?: string | null;
}

/** A call with its result. */
export interface RunPythonFinishedState {
  kind: "finished";
  /** The parsed result, or null when the result is not a run payload: the tool itself failed. */
  payload: RunPythonPayload | null;
  /** Whether the tool result reports success. */
  toolSucceeded: boolean;
  /** The tool result's wall-clock time. */
  runtimeMs: number;
  /** The tool's raw result, for the details section and payload-less results. */
  rawOutput: unknown;
}

interface RunPythonCardProps {
  toolUseId: string;
  /** The call's input, without the server's meta-fields. */
  input: unknown;
  state: RunPythonLiveState | RunPythonFinishedState;
  /** Needed to download the files the run produced; without it they are listed, not offered. */
  threadId?: string;
}

/**
 * A `run_python` call as one card: status, duration, arguments, code, output, and files. Live calls
 * show the code open; finished ones fold it to its first line. A live card leaves its status to
 * the pill above it ({@link RunPythonLiveBadge}).
 */
export function RunPythonCard({
  toolUseId,
  input,
  state,
  threadId,
}: RunPythonCardProps): React.JSX.Element {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const detailsId = React.useId();
  const code = extractRunPythonCode(input);
  const args = extractRunPythonArguments(input);
  const files = state.kind === "finished" ? (state.payload?.outputs ?? []) : [];
  const exitCode =
    state.kind === "finished" ? (state.payload?.exitCode ?? null) : null;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <span className={styles.toolName}>run_python</span>
          <div className={styles.headerStatus}>
            {state.kind === "finished" && (
              <FinishedBadge
                summary={summarizeRunPythonCall({
                  payload: state.payload,
                  toolSucceeded: state.toolSucceeded,
                  runtimeMs: state.runtimeMs,
                  timeoutS: args.timeoutS,
                })}
                exitCode={exitCode}
              />
            )}
            {/* Slot for a future Stop button. */}
          </div>
        </div>
        <div className={styles.headerRow}>
          <ArgumentsLine timeoutS={args.timeoutS} />
          <button
            type="button"
            className={styles.detailsToggle}
            aria-expanded={detailsOpen}
            aria-controls={detailsId}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            {detailsOpen ? (
              <ChevronDown aria-hidden className={styles.toggleIcon} />
            ) : (
              <ChevronRight aria-hidden className={styles.toggleIcon} />
            )}
            Details
          </button>
        </div>
      </div>

      {detailsOpen && (
        <div id={detailsId} className={styles.details}>
          <dl className={styles.detailFacts}>
            <div className={styles.detailFact}>
              <dt>Tool call ID</dt>
              <dd>{toolUseId}</dd>
            </div>
            {exitCode !== null && (
              <div className={styles.detailFact}>
                <dt>Exit code</dt>
                <dd>{exitCode}</dd>
              </div>
            )}
          </dl>
          <JsonSection label="Arguments" value={input} />
          {state.kind === "finished" && (
            <JsonSection label="Raw output" value={state.rawOutput} />
          )}
        </div>
      )}

      {code !== null && (
        <div className={styles.section}>
          <RunPythonCode code={code} defaultOpen={state.kind === "live"} />
        </div>
      )}

      {state.kind === "finished" && (
        <div className={styles.section}>
          {state.payload ? (
            <RunPythonOutput payload={state.payload} />
          ) : (
            <JsonSection label="Output" value={state.rawOutput} />
          )}
        </div>
      )}

      {files.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Files ({files.length})</p>
          {threadId ? (
            <ArtifactStrip
              threadId={threadId}
              toolUseId={toolUseId}
              artifacts={files}
            />
          ) : (
            <FileList files={files} />
          )}
        </div>
      )}
    </div>
  );
}

const LIVE_LOOK: Record<
  RunPythonLivePhase["kind"],
  { tone: StatusTone; icon: LucideIcon }
> = {
  pending: { tone: "neutral", icon: Clock },
  starting: { tone: "info", icon: LoaderCircle },
  running: { tone: "info", icon: LoaderCircle },
  other: { tone: "info", icon: LoaderCircle },
};

/** A live `run_python` call's status: "Pending", "Starting sandbox…", or "Running · m:ss". */
export function RunPythonLiveBadge({
  status,
  activity,
}: Pick<RunPythonLiveState, "status" | "activity">): React.JSX.Element {
  const phase = describeRunPythonLivePhase(status, activity);
  if (phase.kind === "running") {
    // Keyed so the clock starts from zero each time the code starts running.
    return <RunningBadge key="running" />;
  }
  const look = LIVE_LOOK[phase.kind];
  const label = liveLabel(phase);
  return (
    <StatusBadge
      tone={look.tone}
      icon={look.icon}
      label={label}
      aria-label={`Status: ${label}`}
    />
  );
}

function liveLabel(
  phase: Exclude<RunPythonLivePhase, { kind: "running" }>,
): string {
  switch (phase.kind) {
    case "pending":
      return "Pending";
    case "starting":
      return "Starting sandbox…";
    case "other":
      return phase.label;
  }
}

/** "Running · m:ss", counting from when this badge first rendered the running phase. */
function RunningBadge() {
  const [startedAt] = React.useState(() => Date.now());
  const [now, setNow] = React.useState(startedAt);
  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const label = `Running · ${formatClock((now - startedAt) / 1000)}`;
  return (
    <StatusBadge
      tone={LIVE_LOOK.running.tone}
      icon={LIVE_LOOK.running.icon}
      label={label}
      aria-label={`Status: ${label}`}
    />
  );
}

/** The outcome badge (exit code in its tooltip) and the duration with an explanatory tooltip. */
function FinishedBadge({
  summary,
  exitCode,
}: {
  summary: RunPythonCallSummary;
  exitCode: number | null;
}) {
  const look = RUN_PYTHON_OUTCOME_LOOK[summary.outcome.kind];
  const label = summary.outcome.label;
  const exit =
    summary.outcome.kind === "failed" ? describeRunPythonExit(exitCode) : null;
  const explanation = describeRunPythonDuration(summary.duration);
  return (
    <>
      <StatusBadge
        tone={look.tone}
        icon={look.icon}
        label={label}
        tooltip={exit ?? undefined}
        aria-label={exit ? `Status: ${label}, ${exit}` : `Status: ${label}`}
      />
      <Tooltip>
        {/* Not a button: pressing the trigger would close the tooltip until the pointer leaves. */}
        <Tooltip.Trigger
          delay={250}
          closeOnClick={false}
          aria-label={explanation}
          render={
            <span className={styles.duration} tabIndex={0}>
              {formatRunPythonDuration(summary.duration)}
              <Info aria-hidden className={styles.durationIcon} />
            </span>
          }
        />
        <Tooltip.Content>{explanation}</Tooltip.Content>
      </Tooltip>
    </>
  );
}

function ArgumentsLine({ timeoutS }: { timeoutS: number | null }) {
  if (timeoutS === null) {
    // Holds the details toggle at the row's end.
    return <span />;
  }
  return (
    <p className={styles.arguments}>
      <span>timeout {formatLimit(timeoutS)}</span>
    </p>
  );
}

/**
 * The call's code, highlighted. Folded to its first line unless `defaultOpen`; copy always hands
 * out the whole script.
 */
function RunPythonCode({
  code,
  defaultOpen,
}: {
  code: string;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const lines = code.split("\n");
  const hiddenLineCount = lines.length - 1;
  const shown = open ? code : lines[0];

  return (
    <CodeBlock
      className={styles.code}
      code={code}
      language="code · python"
      headerActions={
        hiddenLineCount > 0 && (
          <button
            type="button"
            className={styles.codeToggle}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? (
              <ChevronDown aria-hidden className={styles.toggleIcon} />
            ) : (
              <ChevronRight aria-hidden className={styles.toggleIcon} />
            )}
            {open
              ? "Collapse"
              : `Expand · ${String(hiddenLineCount)} more ${hiddenLineCount === 1 ? "line" : "lines"}`}
          </button>
        )
      }
      highlightedChildren={
        <React.Suspense fallback={<code>{shown}</code>}>
          <HighlightedCodeBody code={shown} language="python" />
        </React.Suspense>
      }
    />
  );
}

/** The files, named with their sizes, where the strip's downloads are not available. */
function FileList({ files }: { files: ArtifactOutput[] }) {
  return (
    <ul className={styles.files} aria-label="Files produced">
      {files.map((file, index) => {
        const returned = file.status === "uploaded";
        return (
          <li
            // Two files can arrive under one name (see `ArtifactStrip`).
            key={`${String(index)}-${file.name}`}
            className={classNames(styles.file, {
              [styles.fileNotReturned]: !returned,
            })}
          >
            <FileText aria-hidden className={styles.fileIcon} />
            <span className={styles.fileName}>{file.name}</span>
            <span className={styles.fileMeta}>
              {formatByteSize(file.bytes)}
              {returned ? "" : ` · ${notReturnedReason(file.status)}`}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
